/**
 * App.jsx — Rently.ma Dashboard
 *
 * Full dashboard application with:
 * - Auth flow (login page + session)
 * - Sidebar navigation
 * - 7 dashboard pages: Overview, Modules, Builds, Logs, Providers, Research, Settings
 * - Real-time polling with ETag support
 * - Responsive layout
 *
 * Built from the original Dashboard.jsx (preserved styling/patterns).
 */

import { useState, useEffect, useCallback, Component } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from './i18n';
import { AuthProvider, useAuth } from './auth/AuthContext';
import { dashboard as api } from './services/api';
import FleetDashboard from './pages/Dashboard';
import FleetManager from './pages/FleetManager';
import RentalsPage from './pages/Rentals';
import NarsaFinesPage from './pages/NarsaFines';
import DgsnReportsPage from './pages/DgsnReports';
import BlacklistPage from './pages/Blacklist';
import B2BNetworkPage from './pages/B2BNetwork';
import LaCaissePage from './pages/LaCaisse';
import Landing from './pages/Landing';

// ── Error Boundary ────────────────────────────────────────────────────
class PageErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Page render error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{padding: '40px', textAlign: 'center', color: '#3454B3'}}>
          <h2 style={{fontSize: '24px', marginBottom: '16px'}}>{i18n.t('system.errorPageTitle')}</h2>
          <p style={{color: '#999', marginBottom: '24px'}}>
            {i18n.t('system.errorPageMessage')}
          </p>
          <button onClick={() => {this.setState({hasError: false, error: null}); window.location.reload();}}
                  style={{padding: '12px 24px', background: '#3454B3', color: '#ffffff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 600}}>
            {i18n.t('system.reloadPage')}
          </button>
          {this.state.error && (
            <details style={{marginTop: '32px', textAlign: 'left', background: 'rgba(52,84,179,0.1)', padding: '16px', borderRadius: '8px'}}>
              <summary style={{cursor: 'pointer', marginBottom: '8px', fontWeight: 600}}>{i18n.t('system.errorDetails')}</summary>
              <pre style={{fontSize: '12px', color: '#666', whiteSpace: 'pre-wrap'}}>{this.state.error.toString()}</pre>
            </details>
          )}
        </div>
      );
    }
    return this.props.children;
  }
}

// ── Styles ────────────────────────────────────────────────────────────
const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@300;400;500;600;700&family=Cinzel:wght@600;700&display=swap');
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { background: var(--bg); color: var(--text); font-family: var(--font-body); }
  ::-webkit-scrollbar { width: 4px; }
  ::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.15); border-radius: 2px; }
  @keyframes pulse { 0%{box-shadow:0 0 0 0 currentColor} 70%{box-shadow:0 0 0 6px transparent} 100%{box-shadow:0 0 0 0 transparent} }
  @keyframes fadeIn { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
  @keyframes spin { to{transform:rotate(360deg)} }
  input::placeholder { color: #9CA3AF; }
  a { color: var(--gold); text-decoration: none; }
`;

const PulseDot = ({ color }) => (
  <span style={{display:'inline-block',position:'relative',width:10,height:10,marginRight:8}}>
    <span style={{display:'block',width:10,height:10,borderRadius:'50%',backgroundColor:color,animation:'pulse 2s infinite'}}/>
  </span>
);

const StatCard = ({ label, value, sub, color='#3454B3', icon }) => (
  <div style={{background:'#FFFFFF',border:'1px solid rgba(0,0,0,0.05)',boxShadow:'0 2px 8px rgba(0,0,0,0.04), 0 0 1px rgba(0,0,0,0.06)',
    borderRadius:12,padding:'20px 24px',display:'flex',flexDirection:'column',gap:6}}>
    <span style={{fontSize:22}}>{icon}</span>
    <span style={{fontSize:28,fontWeight:700,color,letterSpacing:-1}}>{value}</span>
    <span style={{fontSize:11,color:'#6B7280',textTransform:'uppercase',letterSpacing:2}}>{label}</span>
    {sub && <span style={{fontSize:12,color:'#9CA3AF'}}>{sub}</span>}
  </div>
);

// ── Login Page ──────────────────────────────────────────────────────
function LoginPage({ onBack }) {
  const { login, error } = useAuth();
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailFocused, setEmailFocused] = useState(false);
  const [passFocused, setPassFocused] = useState(false);

  // Let i18n/index.js applyDirection() control dir/lang — no override needed.
  // Login page is already translated (login.* keys) and respects RTL for AR.

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    await login(email, password);
    setLoading(false);
  };

  const changeLang = (lng) => i18n.changeLanguage(lng);
  const activeLang = i18n.language?.substring(0, 2) || 'fr';

  const inputStyle = (focused) => ({
    width: '100%',
    height: 48,
    padding: '0 14px',
    background: 'var(--elevated)',
    border: `1px solid ${focused ? 'var(--gold)' : 'var(--border)'}`,
    borderRadius: 8,
    color: 'var(--text)',
    fontSize: 14,
    outline: 'none',
    fontFamily: 'inherit',
    transition: 'border-color 200ms',
  });

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      background: 'var(--bg)',
      animation: 'fadeIn 0.5s ease',
    }}>
      <div style={{
        maxWidth: 400,
        width: '100%',
        margin: '0 16px',
        padding: 40,
        background: 'var(--card)',
        border: '1px solid var(--border)',
        borderRadius: 16,
        borderTop: '4px solid var(--gold)',
      }}>
        {/* Back Button */}
        {onBack && (
          <button
            onClick={onBack}
            style={{
              position: 'absolute',
              top: 24,
              insetInlineStart: 24,
              background: 'transparent',
              border: '1px solid var(--border)',
              color: 'var(--text-muted)',
              fontSize: 12,
              padding: '8px 16px',
              borderRadius: 6,
              cursor: 'pointer',
              transition: 'all 150ms',
            }}
            onMouseEnter={e => {
              e.target.style.borderColor = 'var(--border-gold)';
              e.target.style.color = 'var(--gold)';
            }}
            onMouseLeave={e => {
              e.target.style.borderColor = 'var(--border)';
              e.target.style.color = 'var(--text-muted)';
            }}
          >
            {t('login.backToHome')}
          </button>
        )}

        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <img
            src="/brand/rently-lockup-teal.svg"
            alt="Rently.ma"
            style={{ height: 48, width: 'auto' }}
          />
          <div style={{
            fontSize: 11,
            color: 'var(--text-secondary)',
            textAlign: 'center',
            marginTop: 10,
            letterSpacing: '0.15em',
            textTransform: 'uppercase',
            fontWeight: 600,
          }}>
            {t('login.subtitle')}
          </div>
          <div style={{
            fontSize: 10,
            color: 'var(--text-muted)',
            textAlign: 'center',
            marginTop: 6,
            letterSpacing: '0.05em',
          }}>
            {t('login.regionTag')}
          </div>
        </div>

        {/* Error */}
        {error && (
          <div style={{
            color: 'var(--red)',
            fontSize: 13,
            marginBottom: 16,
            textAlign: 'center',
          }}>
            {typeof error === 'string' ? error
              : error instanceof Error ? error.message
              : error?.error || (typeof error?.detail === 'string' ? error.detail : null) || t('login.error')}
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <input
            value={email}
            onChange={e => setEmail(e.target.value)}
            onFocus={() => setEmailFocused(true)}
            onBlur={() => setEmailFocused(false)}
            placeholder={t('login.email')}
            type="email"
            style={inputStyle(emailFocused)}
          />
          <input
            value={password}
            onChange={e => setPassword(e.target.value)}
            onFocus={() => setPassFocused(true)}
            onBlur={() => setPassFocused(false)}
            placeholder={t('login.password')}
            type="password"
            style={inputStyle(passFocused)}
          />
          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              height: 48,
              marginTop: 8,
              background: loading ? 'var(--text-muted)' : 'var(--gold)',
              color: '#ffffff',
              border: 'none',
              borderRadius: 8,
              fontSize: 14,
              fontWeight: 600,
              cursor: loading ? 'not-allowed' : 'pointer',
              letterSpacing: 1,
              filter: 'brightness(1)',
              transition: 'filter 200ms',
            }}
            onMouseEnter={e => { if (!loading) e.target.style.filter = 'brightness(1.1)'; }}
            onMouseLeave={e => { e.target.style.filter = 'brightness(1)'; }}
          >
            {loading ? t('login.authenticating') : t('login.submit')}
          </button>
        </form>

        {/* Language toggle */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 24 }}>
          {['fr', 'ar', 'en'].map(lng => (
            <button key={lng} type="button" onClick={() => changeLang(lng)}
              style={{
                padding: '4px 12px',
                fontSize: 11,
                fontWeight: activeLang === lng ? 700 : 400,
                background: activeLang === lng ? 'rgba(52,84,179,0.15)' : 'transparent',
                color: activeLang === lng ? 'var(--gold)' : 'var(--text-muted)',
                border: `1px solid ${activeLang === lng ? 'rgba(52,84,179,0.3)' : 'var(--border)'}`,
                borderRadius: 6,
                cursor: 'pointer',
                fontFamily: 'inherit',
                textTransform: 'uppercase',
                letterSpacing: 1,
              }}>
              {lng}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── SVG Icon Helper (matches dashboard.html icons) ──────────────────
const SvgIcon = ({ children }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"
    fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    style={{flexShrink:0}}>
    {children}
  </svg>
);

// ── Sidebar Navigation ──────────────────────────────────────────────
// Section 1: Agency product
const AGENCY_NAV = [
  { key: 'fleet', labelKey: 'nav.dashboard', icon: <SvgIcon><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></SvgIcon> },
  { key: 'fleet-mgr', labelKey: 'nav.fleet', icon: <SvgIcon><path d="M5 17H3a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v5"/><circle cx="16" cy="17" r="2"/><circle cx="7" cy="17" r="2"/></SvgIcon> },
  { key: 'rentals-page', labelKey: 'nav.rentals', icon: <SvgIcon><path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/></SvgIcon> },
  { key: 'narsa-page', labelKey: 'nav.narsa', icon: <SvgIcon><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></SvgIcon> },
  { key: 'dgsn', labelKey: 'nav.dgsn', icon: <SvgIcon><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></SvgIcon> },
  { key: 'blacklist', labelKey: 'nav.blacklist', icon: <SvgIcon><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><line x1="9.5" y1="9.5" x2="14.5" y2="14.5"/><line x1="14.5" y1="9.5" x2="9.5" y2="14.5"/></SvgIcon> },
  { key: 'b2b', labelKey: 'nav.b2b', icon: <SvgIcon><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></SvgIcon> },
  { key: 'caisse', labelKey: 'nav.caisse', icon: <SvgIcon><rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/></SvgIcon> },
];

const AGENCY_KEYS = AGENCY_NAV.map(n => n.key);

// ── Mobile Bottom Tab Bar (visible ≤768px via CSS class in index.css) ──
const MOBILE_TABS = [
  { key: 'fleet', labelKey: 'nav.dashboard', icon: <SvgIcon><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></SvgIcon> },
  { key: 'fleet-mgr', labelKey: 'nav.fleet', icon: <SvgIcon><path d="M5 17H3a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v5"/><circle cx="16" cy="17" r="2"/><circle cx="7" cy="17" r="2"/></SvgIcon> },
  { key: 'rentals-page', labelKey: 'nav.rentals', icon: <SvgIcon><path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/></SvgIcon> },
  { key: 'narsa-page', labelKey: 'nav.narsa', icon: <SvgIcon><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></SvgIcon> },
];

const MORE_TABS = [
  { key: 'caisse', labelKey: 'nav.caisse', icon: <SvgIcon><rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/></SvgIcon> },
  { key: 'dgsn', labelKey: 'nav.dgsn', icon: <SvgIcon><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></SvgIcon> },
  { key: 'blacklist', labelKey: 'nav.blacklist', icon: <SvgIcon><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><line x1="9.5" y1="9.5" x2="14.5" y2="14.5"/><line x1="14.5" y1="9.5" x2="9.5" y2="14.5"/></SvgIcon> },
  { key: 'b2b', labelKey: 'nav.b2b', icon: <SvgIcon><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></SvgIcon> },
];

function MobileTabBar({ active, onNavigate }) {
  const { t } = useTranslation();
  const [moreOpen, setMoreOpen] = useState(false);
  const isMoreActive = MORE_TABS.some(tab => tab.key === active);

  return (
    <div className="bottom-tab-bar">
      {/* Backdrop to dismiss More popover */}
      {moreOpen && <div onClick={() => setMoreOpen(false)} style={{
        position:'fixed', inset:0, zIndex:150, background:'transparent'
      }}/>}
      {MOBILE_TABS.map(tab => (
        <button key={tab.key} className={`tab-item${active === tab.key ? ' active' : ''}`}
          onClick={() => { setMoreOpen(false); onNavigate(tab.key); }}
          style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}>
          <span className="tab-item-icon">{tab.icon}</span>
          {t(tab.labelKey)}
        </button>
      ))}
      {/* More tab with popover */}
      <div style={{ position: 'relative', zIndex: 160 }}>
        <button className={`tab-item${isMoreActive ? ' active' : ''}`}
          onClick={() => setMoreOpen(!moreOpen)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}>
          <span className="tab-item-icon">
            <SvgIcon><circle cx="12" cy="5" r="1.5"/><circle cx="12" cy="12" r="1.5"/><circle cx="12" cy="19" r="1.5"/></SvgIcon>
          </span>
          {t('nav.more')}
        </button>
        {moreOpen && (
          <div style={{
            position: 'absolute', bottom: '100%', right: 0,
            marginBottom: 8, minWidth: 180,
            background: '#FFFFFF', border: '1px solid rgba(0,0,0,0.08)',
            borderRadius: 12, boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
            padding: '6px 0', zIndex: 200,
          }}>
            {MORE_TABS.map(tab => (
              <button key={tab.key}
                onClick={() => { setMoreOpen(false); onNavigate(tab.key); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                  padding: '10px 16px', background: active === tab.key ? 'rgba(52,84,179,0.06)' : 'transparent',
                  border: 'none', cursor: 'pointer', fontFamily: 'inherit',
                  fontSize: 13, fontWeight: active === tab.key ? 600 : 400,
                  color: active === tab.key ? '#3454B3' : '#4A5568',
                  textAlign: 'left',
                }}>
                <span style={{ display: 'flex', alignItems: 'center', color: active === tab.key ? '#3454B3' : '#9CA3AF' }}>
                  {tab.icon}
                </span>
                {t(tab.labelKey)}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Mobile Top Header (visible ≤768px — shows logo when sidebar is hidden) ──
function MobileHeader() {
  return (
    <div className="mobile-header">
      <img src="/brand/rently-lockup-teal.svg" alt="Rently.ma"
        style={{ height: 28, width: 'auto', objectFit: 'contain' }} />
    </div>
  );
}

function Sidebar({ active, onNavigate, user, onLogout }) {
  const { t, i18n } = useTranslation();
  const activeLang = i18n.language?.substring(0, 2) || 'fr';
  const changeLang = (lng) => i18n.changeLanguage(lng);

  const navBtn = (item) => (
    <button key={item.key} onClick={()=>onNavigate(item.key)}
      style={{width:'100%',display:'flex',alignItems:'center',gap:10,padding:'10px 12px',
        background:active===item.key?'rgba(52,84,179,0.08)':'transparent',
        border:active===item.key?'1px solid rgba(52,84,179,0.15)':'1px solid transparent',
        borderRadius:8,color:active===item.key?'#3454B3':'#4A5568',
        cursor:'pointer',fontSize:12,marginBottom:4,textAlign:'left',fontFamily:'inherit',
        transition:'all 0.2s'}}>
      <span style={{display:'flex',alignItems:'center'}}>{item.icon}</span>
      <span style={{flex:1}}>{t(item.labelKey)}</span>
      {item.badge && (
        <span style={{background:'#EF4444',color:'#fff',fontSize:10,fontWeight:700,
          padding:'2px 7px',borderRadius:10,lineHeight:'14px'}}>{item.badge}</span>
      )}
    </button>
  );

  return (
    <div className="sidebar" style={{width:'var(--sidebar-w)',minHeight:'100vh',background:'#FFFFFF',
      borderInlineEnd:'1px solid rgba(0,0,0,0.05)',
      boxShadow:'2px 0 8px rgba(0,0,0,0.03)',
      display:'flex',flexDirection:'column',position:'fixed',insetInlineStart:0,top:0,zIndex:10,overflow:'hidden',transition:'width 0.2s ease'}}>
      <div style={{padding:'12px 14px',borderBottom:'1px solid rgba(0,0,0,0.05)',display:'flex',alignItems:'center',justifyContent:'center',minHeight:52}}>
        <img src="/brand/rently-lockup-teal.svg" alt="Rently.ma" className="sidebar-logo-full"
          style={{height:32,width:'auto',objectFit:'contain'}} />
        <img src="/brand/rently-symbol-teal.svg" alt="Rently" className="sidebar-logo-symbol"
          style={{height:32,width:32,objectFit:'contain'}} />
      </div>
      <nav style={{flex:1,padding:'12px 8px',overflowY:'auto'}}>
        {AGENCY_NAV.map(navBtn)}
      </nav>
      {/* Language Toggle */}
      <div style={{padding:'8px 16px',borderTop:'1px solid rgba(0,0,0,0.05)',display:'flex',justifyContent:'center',gap:6}}>
        {['fr','ar','en'].map(lng => (
          <button key={lng} type="button" onClick={()=>changeLang(lng)}
            style={{padding:'4px 10px',fontSize:10,fontWeight:activeLang===lng?700:400,
              background:activeLang===lng?'rgba(52,84,179,0.15)':'transparent',
              color:activeLang===lng?'#3454B3':'#6B7280',
              border:`1px solid ${activeLang===lng?'rgba(52,84,179,0.25)':'rgba(0,0,0,0.10)'}`,
              borderRadius:5,cursor:'pointer',fontFamily:'inherit',textTransform:'uppercase',letterSpacing:1}}>
            {lng}
          </button>
        ))}
      </div>
      <div style={{padding:'16px',borderTop:'1px solid rgba(0,0,0,0.05)'}}>
        <div style={{fontSize:11,color:'#4A5568',marginBottom:8}}>{user?.sub || 'User'}</div>
        <div style={{fontSize:10,color:'#9CA3AF',marginBottom:12}}>{t('common.role')}: {t(`roles.${user?.role}`) || user?.role || '—'}</div>
        <button onClick={onLogout}
          style={{width:'100%',padding:'8px',background:'transparent',border:'1px solid rgba(0,0,0,0.12)',
            borderRadius:6,color:'#4A5568',cursor:'pointer',fontSize:11,fontFamily:'inherit'}}>
          {t('common.logout')}
        </button>
      </div>
    </div>
  );
}

// ── Overview Page ───────────────────────────────────────────────────
function OverviewPage({ status, modules, builds, generating, description, onDescChange, onGenerate }) {
  const s = status;
  return (
    <div style={{animation:'fadeIn 0.3s ease'}}>
      <h2 style={{fontSize:18,fontWeight:600,color:'#3454B3',marginBottom:20,letterSpacing:1}}>System Overview</h2>
      <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:12,marginBottom:24}}>
        <StatCard icon="🗄️" label="Database" value={s?.database?.status||'—'} color={s?.database?.healthy?'#4ADE80':'#EF4444'} sub={`Docker: ${s?.docker?.running?'running':'stopped'}`}/>
        <StatCard icon="💾" label="RAM Free" value={`${s?.ram?.free_gb||'—'}GB`} color={s?.ram?.warning?'#F59E0B':'#4ADE80'} sub={`${s?.ram?.total_gb}GB total`}/>
        <StatCard icon="⚙️" label="Modules" value={s?.modules?.count||0} color="#3454B3" sub="active"/>
        <StatCard icon="📦" label="Builds" value={s?.builds?.count||0} color="#818CF8" sub="in output/"/>
      </div>
      {/* RAM Bar */}
      {s?.ram && <div style={{background:'#FFFFFF',border:'1px solid rgba(0,0,0,0.08)',borderRadius:12,padding:'16px 24px',marginBottom:24,boxShadow:'0 1px 3px rgba(0,0,0,0.04)'}}>
        <div style={{display:'flex',justifyContent:'space-between',marginBottom:4}}>
          <span style={{fontSize:10,color:'#9CA3AF',letterSpacing:1}}>RAM USAGE</span>
          <span style={{fontSize:10,color:s.ram.used_pct>90?'#EF4444':s.ram.used_pct>75?'#F59E0B':'#4ADE80',fontFamily:'monospace'}}>{s.ram.used_pct}%</span>
        </div>
        <div style={{height:4,background:'rgba(0,0,0,0.06)',borderRadius:2}}>
          <div style={{height:4,width:`${s.ram.used_pct}%`,background:s.ram.used_pct>90?'#EF4444':'#4ADE80',borderRadius:2,transition:'width 1s'}}/>
        </div>
      </div>}
      {/* Generate */}
      <div style={{background:'rgba(52,84,179,0.05)',border:'1px solid rgba(52,84,179,0.25)',borderRadius:12,padding:'16px 20px'}}>
        <div style={{fontSize:11,color:'#3454B3',letterSpacing:2,textTransform:'uppercase',marginBottom:12}}>Generate Feature</div>
        <div style={{display:'flex',gap:10}}>
          <input value={description} onChange={e=>onDescChange(e.target.value)} onKeyDown={e=>e.key==='Enter'&&onGenerate()}
            placeholder='e.g. "Login endpoint with JWT for owner manager agent roles"'
            style={{flex:1,background:'rgba(0,0,0,0.4)',border:'1px solid rgba(255,255,255,0.1)',borderRadius:8,padding:'10px 14px',color:'#E5E7EB',fontSize:12,outline:'none',fontFamily:'inherit'}}/>
          <button onClick={onGenerate} disabled={generating||!description.trim()}
            style={{padding:'10px 24px',background:generating?'#374151':'#3454B3',color:generating?'#9CA3AF':'#fff',border:'none',borderRadius:8,fontSize:12,fontWeight:700,cursor:generating?'not-allowed':'pointer',letterSpacing:1}}>
            {generating?'BUILDING...':'BUILD →'}
          </button>
        </div>
        <div style={{fontSize:10,color:'#4B5563',marginTop:8}}>Council: 🔴 Architect (Opus) → 🟡 Dev (Sonnet) → 🟢 QA (Haiku)</div>
      </div>
    </div>
  );
}

// ── Modules Page ────────────────────────────────────────────────────
function ModulesPage({ modules, onKill, confirmingKill, onCancelKill }) {
  return (
    <div style={{animation:'fadeIn 0.3s ease'}}>
      <h2 style={{fontSize:18,fontWeight:600,color:'#3454B3',marginBottom:20}}>Backend Modules</h2>
      <div style={{fontSize:11,color:'#4B5563',marginBottom:12}}>
        Active .py files in backend/ with router = APIRouter(). {modules.length} loaded.
      </div>
      {modules.map(mod => (
        <div key={mod.name} style={{display:'flex',alignItems:'center',gap:12,padding:'12px 16px',borderRadius:8,
          background:'rgba(255,255,255,0.02)',border:'1px solid rgba(255,255,255,0.06)',marginBottom:6}}>
          <PulseDot color={mod.has_router?'#4ADE80':'#FCD34D'}/>
          <div style={{flex:1}}>
            <div style={{fontSize:13,color:'#E5E7EB'}}>{mod.name}</div>
            <div style={{fontSize:11,color:'#6B7280',marginTop:2}}>
              {mod.endpoints?.slice(0,3).join(' · ')||'no endpoints'}
              {mod.endpoints?.length>3&&` + ${mod.endpoints.length-3} more`}
            </div>
          </div>
          <span style={{fontSize:11,color:'#4B5563'}}>{mod.size_kb}KB</span>
          {!mod.core && (
            confirmingKill === mod.name ? (
              <div style={{display:'flex',gap:6}}>
                <button onClick={()=>onKill(mod.name)}
                  style={{padding:'4px 10px',background:'#EF4444',color:'#fff',border:'none',borderRadius:6,fontSize:11,cursor:'pointer',fontWeight:600,fontFamily:'inherit'}}>
                  Confirm Kill
                </button>
                <button onClick={onCancelKill}
                  style={{padding:'4px 10px',background:'transparent',color:'#9CA3AF',border:'1px solid #374151',borderRadius:6,fontSize:11,cursor:'pointer',fontFamily:'inherit'}}>
                  Cancel
                </button>
              </div>
            ) : (
              <button onClick={()=>onKill(mod.name)}
                style={{padding:'4px 12px',background:'transparent',color:'#6B7280',border:'1px solid #374151',borderRadius:6,fontSize:11,cursor:'pointer',fontFamily:'inherit'}}>
                Kill
              </button>
            )
          )}
        </div>
      ))}
    </div>
  );
}

// ── Builds Page ─────────────────────────────────────────────────────
function BuildsPage({ builds, onApply }) {
  const [selected, setSelected] = useState(null);
  return (
    <div style={{animation:'fadeIn 0.3s ease'}}>
      <h2 style={{fontSize:18,fontWeight:600,color:'#3454B3',marginBottom:20}}>Build History</h2>
      {builds.map(b => {
        const badges = [b.has_brief&&{l:'ARCH',c:'#818CF8'},b.has_code&&{l:'CODE',c:'#4ADE80'},
          b.has_qa&&{l:'QA',c:'#FCD34D'},b.has_preflight&&{l:'PRE',c:'#60A5FA'}].filter(Boolean);
        const isSelected = selected===b.id;
        return (
          <div key={b.id} onClick={()=>setSelected(isSelected?null:b.id)}
            style={{padding:'12px 16px',borderRadius:8,cursor:'pointer',
              background:isSelected?'rgba(52,84,179,0.08)':'rgba(255,255,255,0.02)',
              border:`1px solid ${isSelected?'rgba(52,84,179,0.4)':'rgba(255,255,255,0.06)'}`,marginBottom:6}}>
            <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:6}}>
              <PulseDot color={b.complete?'#4ADE80':'#FCD34D'}/>
              <span style={{fontSize:11,color:'#9CA3AF',flex:1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                {b.id.replace(/^\d{8}_\d{6}_/,'')}
              </span>
              <span style={{fontSize:11,color:'#4B5563'}}>{new Date(b.created).toLocaleString()}</span>
            </div>
            <div style={{display:'flex',gap:6,flexWrap:'wrap',marginBottom:isSelected?8:0}}>
              {badges.map(bg=>(<span key={bg.l} style={{padding:'2px 8px',background:`${bg.c}20`,color:bg.c,borderRadius:4,fontSize:10,fontWeight:600,letterSpacing:1}}>{bg.l}</span>))}
            </div>
            {isSelected && <button onClick={e=>{e.stopPropagation();onApply(b.id)}}
              style={{width:'100%',padding:8,background:'#3454B3',color:'#fff',border:'none',borderRadius:6,fontSize:12,fontWeight:700,cursor:'pointer',letterSpacing:1}}>
              APPLY TO PRODUCTION
            </button>}
          </div>
        );
      })}
      {builds.length===0 && <div style={{color:'#4B5563',textAlign:'center',padding:40}}>No builds yet</div>}
    </div>
  );
}

// ── Logs Page ───────────────────────────────────────────────────────
function LogsPage({ logs }) {
  const colorLine = l => {
    if(l.includes('✅')||l.includes('PASS'))return'#4ADE80';
    if(l.includes('❌')||l.includes('FAIL')||l.includes('Error'))return'#F87171';
    if(l.includes('⚠️')||l.includes('WARNING'))return'#FCD34D';
    if(l.includes('PHASE'))return'#3454B3';
    return'#9CA3AF';
  };
  return (
    <div style={{animation:'fadeIn 0.3s ease'}}>
      <h2 style={{fontSize:18,fontWeight:600,color:'#3454B3',marginBottom:20}}>Build Logs</h2>
      <div style={{fontFamily:'monospace',fontSize:11,lineHeight:1.7,height:500,overflowY:'auto',padding:'12px 16px',
        background:'rgba(0,0,0,0.4)',borderRadius:8,border:'1px solid rgba(255,255,255,0.06)'}}>
        {logs.length===0?<span style={{color:'#4B5563'}}>No logs yet</span>
          :logs.map((l,i)=><div key={i} style={{color:colorLine(l),whiteSpace:'pre-wrap',wordBreak:'break-all'}}>{l||' '}</div>)}
      </div>
    </div>
  );
}

// ── Providers Page ──────────────────────────────────────────────────
function ProvidersPage({ providers }) {
  const statusColor = s => ({healthy:'#4ADE80',degraded:'#F59E0B',down:'#EF4444'}[s]||'#6B7280');
  return (
    <div style={{animation:'fadeIn 0.3s ease'}}>
      <h2 style={{fontSize:18,fontWeight:600,color:'#3454B3',marginBottom:20}}>AI Providers</h2>
      <div style={{display:'grid',gridTemplateColumns:'repeat(2,1fr)',gap:12,marginBottom:24}}>
        {(providers?.providers||[]).map(p=>(
          <div key={p.provider} style={{background:'rgba(255,255,255,0.03)',border:'1px solid rgba(255,255,255,0.08)',borderRadius:12,padding:'20px 24px'}}>
            <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:12}}>
              <PulseDot color={statusColor(p.status)}/>
              <span style={{fontSize:14,fontWeight:600,color:'#E5E7EB',textTransform:'capitalize'}}>{p.provider}</span>
              <span style={{fontSize:10,color:statusColor(p.status),marginLeft:'auto'}}>{p.status}</span>
            </div>
            {p.latency_ms>0&&<div style={{fontSize:11,color:'#6B7280'}}>Latency: {p.latency_ms}ms</div>}
            {p.models_available?.length>0&&<div style={{fontSize:11,color:'#4B5563',marginTop:4}}>
              Models: {p.models_available.join(', ')}
            </div>}
            {p.error&&<div style={{fontSize:11,color:'#F87171',marginTop:4}}>{p.error}</div>}
          </div>
        ))}
      </div>
      {providers?.routing_policy && <div>
        <h3 style={{fontSize:14,color:'#9CA3AF',marginBottom:12}}>Task Routing Policy</h3>
        <div style={{background:'rgba(0,0,0,0.3)',borderRadius:8,padding:16,fontSize:11}}>
          {Object.entries(providers.routing_policy).map(([task,chain])=>(
            <div key={task} style={{marginBottom:4}}>
              <span style={{color:'#3454B3'}}>{task}</span>
              <span style={{color:'#4B5563'}}> → </span>
              <span style={{color:'#9CA3AF'}}>{chain.join(' → ')}</span>
            </div>
          ))}
        </div>
      </div>}
    </div>
  );
}

// ── Research Page ───────────────────────────────────────────────────
function ResearchPage({ research }) {
  return (
    <div style={{animation:'fadeIn 0.3s ease'}}>
      <h2 style={{fontSize:18,fontWeight:600,color:'#3454B3',marginBottom:20}}>Research Snapshots</h2>
      {(research?.snapshots||[]).length===0
        ?<div style={{color:'#4B5563',textAlign:'center',padding:40}}>No research snapshots yet. Run a market analysis to populate.</div>
        :(research.snapshots||[]).map(s=>(
          <div key={s.id} style={{background:'rgba(255,255,255,0.02)',border:'1px solid rgba(255,255,255,0.06)',borderRadius:8,padding:'16px',marginBottom:8}}>
            <div style={{fontSize:14,color:'#E5E7EB',fontWeight:500}}>{s.title}</div>
            <div style={{fontSize:11,color:'#6B7280',marginTop:4}}>{s.summary}</div>
            <div style={{fontSize:10,color:'#4B5563',marginTop:8}}>
              {s.created} · {s.citations} citation(s)
            </div>
          </div>
        ))
      }
    </div>
  );
}

// ── Settings Page ───────────────────────────────────────────────────
function SettingsPage({ settings }) {
  const s = settings||{};
  const bool = v => v==='true'||v===true;
  return (
    <div style={{animation:'fadeIn 0.3s ease'}}>
      <h2 style={{fontSize:18,fontWeight:600,color:'#3454B3',marginBottom:20}}>Configuration</h2>
      <div style={{display:'grid',gridTemplateColumns:'repeat(2,1fr)',gap:12}}>
        <StatCard icon="🔒" label="Production Mode" value={bool(s.production_mode)?'ON':'OFF'} color={bool(s.production_mode)?'#4ADE80':'#F59E0B'}/>
        <StatCard icon="🐛" label="Debug" value={bool(s.debug)?'ON':'OFF'} color={bool(s.debug)?'#F59E0B':'#4ADE80'}/>
        <StatCard icon="🗄️" label="Database" value={s.database_configured?'Configured':'Missing'} color={s.database_configured?'#4ADE80':'#EF4444'}/>
        <StatCard icon="🔑" label="JWT" value={s.jwt_configured?'Configured':'Missing'} color={s.jwt_configured?'#4ADE80':'#EF4444'}/>
      </div>
      {s.providers_configured && <div style={{marginTop:24}}>
        <h3 style={{fontSize:14,color:'#9CA3AF',marginBottom:12}}>Provider API Keys</h3>
        <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:8}}>
          {Object.entries(s.providers_configured).map(([name,configured])=>(
            <div key={name} style={{background:'rgba(255,255,255,0.03)',border:'1px solid rgba(255,255,255,0.06)',borderRadius:8,padding:'12px',textAlign:'center'}}>
              <div style={{fontSize:12,color:'#E5E7EB',textTransform:'capitalize',marginBottom:4}}>{name}</div>
              <div style={{fontSize:20}}>{configured?'✅':'❌'}</div>
            </div>
          ))}
        </div>
      </div>}
    </div>
  );
}

// ── Placeholder Page (for unbuilt agency pages) ────────────────────
function PlaceholderPage({ title, subtitle }) {
  const { t } = useTranslation();
  return (
    <div style={{animation:'fadeIn 0.3s ease'}}>
      <h2 style={{fontSize:18,fontWeight:600,color:'#3454B3',marginBottom:8,letterSpacing:1}}>{title}</h2>
      {subtitle && <p style={{fontSize:13,color:'#6B7280',marginBottom:24}}>{subtitle}</p>}
      <div style={{padding:'60px 40px',textAlign:'center',background:'rgba(255,255,255,0.02)',
        border:'1px solid rgba(255,255,255,0.06)',borderRadius:12}}>
        <div style={{fontSize:32,marginBottom:16,opacity:0.3}}>🚧</div>
        <div style={{fontSize:14,color:'#6B7280',marginBottom:8}}>{t('placeholder.underConstruction')}</div>
        <div style={{fontSize:12,color:'#4B5563'}}>{t('placeholder.comingSoonVersion')}</div>
      </div>
    </div>
  );
}

// ── Main Dashboard ──────────────────────────────────────────────────
function Dashboard() {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const [page, setPage] = useState('fleet');
  const [status, setStatus] = useState(null);
  const [modules, setModules] = useState([]);
  const [builds, setBuilds] = useState([]);
  const [logs, setLogs] = useState([]);
  const [providers, setProviders] = useState(null);
  const [research, setResearch] = useState(null);
  const [settings, setSettings] = useState(null);
  const [description, setDescription] = useState('');
  const [generating, setGenerating] = useState(false);
  const [toast, setToast] = useState(null);
  const [connected, setConnected] = useState(false);
  const [confirmingKill, setConfirmingKill] = useState(null);

  const showToast = (msg, type='success') => {
    setToast({msg,type});
    setTimeout(()=>setToast(null),4000);
  };

  const fetchAll = useCallback(async () => {
    try {
      const [s,m,b,l] = await Promise.all([
        api.status(), api.modules(), api.builds(), api.logs(80),
      ]);
      if(s) setStatus(s);
      if(m) setModules(m.modules||[]);
      if(b) setBuilds(b.builds||[]);
      if(l) setLogs(l.lines||[]);
      setConnected(true);
    } catch {
      setConnected(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
    const iv = setInterval(fetchAll, 8000); // 8s instead of 6s to reduce waste
    return () => clearInterval(iv);
  }, [fetchAll]);

  // Lazy-load provider/research/settings data
  useEffect(() => {
    if(page==='providers') api.providers().then(setProviders).catch(()=>{});
    if(page==='research') api.research().then(setResearch).catch(()=>{});
    if(page==='settings') api.settings().then(setSettings).catch(()=>{});
  }, [page]);

  const handleGenerate = async () => {
    if(!description.trim()||generating) return;
    setGenerating(true);
    try {
      await api.generate(description);
      setDescription('');
      showToast('Generation started');
      setPage('logs');
      setTimeout(fetchAll,3000);
    } catch(e) {
      showToast(e.message||'API error','error');
    }
    setGenerating(false);
  };

  const handleKill = async (name) => {
    // Issue 18 fix: two-phase state-based kill confirmation
    if (confirmingKill !== name) {
      setConfirmingKill(name);
      return;
    }
    setConfirmingKill(null);
    try {
      await api.killModule(name);
      showToast(name + ' removed','warning');
      fetchAll();
    } catch { showToast('Kill failed','error'); }
  };

  const handleApply = async (id) => {
    try {
      const r = await api.apply(id);
      showToast(`Applied ${r.files_applied?.length||0} file(s). Restart to activate.`);
      fetchAll();
    } catch(e) { showToast(e.message||'Apply failed','error'); }
  };

  // Mock data for preview mode
  const mockStatus = {ram:{total_gb:15.7,free_gb:3.4,used_pct:78,warning:true},database:{status:'healthy',healthy:true},docker:{running:true},api:{healthy:true},modules:{count:3},builds:{count:7}};
  const mockModules = [
    {name:'dashboard.py',has_router:true,endpoints:['GET /status','GET /modules','POST /generate'],size_kb:6.2,core:true},
    {name:'fleet.py',has_router:true,endpoints:['GET /vehicles','POST /vehicles'],size_kb:5.8,core:false},
  ];
  const s = connected ? status : mockStatus;
  const ms = connected ? modules : (modules.length?modules:mockModules);
  const bs = connected ? builds : builds;
  const ls = connected ? logs : [];

  const renderPage = () => {
    switch(page) {
      // Agency pages
      case 'fleet': return <FleetDashboard onNavigate={setPage} />;
      case 'fleet-mgr': return <FleetManager />;
      case 'rentals-page': return <RentalsPage />;
      case 'narsa-page': return <NarsaFinesPage />;
      case 'dgsn': return <DgsnReportsPage />;
      case 'blacklist': return <BlacklistPage />;
      case 'b2b': return <B2BNetworkPage />;
      case 'caisse': return <LaCaissePage />;
      default: return null;
    }
  };

  return (
    <div style={{display:'flex',minHeight:'100vh'}}>
      <style>{STYLES}</style>
      <Sidebar active={page} onNavigate={setPage} user={user} onLogout={logout}/>
      <MobileHeader/>
      <MobileTabBar active={page} onNavigate={setPage}/>
      <main style={{marginInlineStart:'var(--sidebar-w)',flex:1,padding:'24px 32px',transition:'margin-left 0.2s ease'}}>
        {/* Spacer — connection status is shown inside each page */}
        {/* Toast */}
        {toast && <div style={{position:'fixed',top:20,right:20,zIndex:1000,padding:'12px 20px',borderRadius:8,
          background:toast.type==='error'?'#EF4444':toast.type==='warning'?'#F59E0B':'#4ADE80',
          color:'#fff',fontWeight:600,fontSize:13,animation:'fadeIn 0.3s',boxShadow:'0 8px 32px rgba(0,0,0,0.4)'}}>
          {toast.msg}
        </div>}
        <PageErrorBoundary>
          {renderPage()}
        </PageErrorBoundary>
        {/* Footer */}
        <div style={{marginTop:32,paddingTop:16,borderTop:'1px solid rgba(0,0,0,0.04)',display:'flex',justifyContent:'space-between'}}>
          <span style={{fontSize:10,color:'#9CA3AF'}}>Rently.ma AI Engine</span>
          <a href="/docs" style={{fontSize:10,color:'#6B7280'}}>API DOCS →</a>
        </div>
      </main>
    </div>
  );
}

// ── App Root ────────────────────────────────────────────────────────
export default function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

function AppContent() {
  const { user, loading } = useAuth();
  const { t } = useTranslation();
  const [view, setView] = useState('landing'); // 'landing' or 'login'
  
  if (loading) return <div style={{minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center',background:'var(--bg)',color:'#3454B3',fontSize:14}}>{t('common.loading')}</div>;
  
  if (user) return <AgencyApp />;
  
  // Unauthenticated: show landing or login
  return view === 'landing' ? <Landing onNavigate={setView} /> : <LoginPage onBack={() => setView('landing')} />;
}

// ── Agency App (main app for logged-in users) ──────────────────────
function AgencyApp() {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const [page, setPage] = useState('fleet');

  const renderPage = () => {
    switch(page) {
      case 'fleet': return <FleetDashboard onNavigate={setPage} />;
      case 'fleet-mgr': return <FleetManager />;
      case 'rentals-page': return <RentalsPage />;
      case 'narsa-page': return <NarsaFinesPage />;
      case 'dgsn': return <DgsnReportsPage />;
      case 'blacklist': return <BlacklistPage />;
      case 'b2b': return <B2BNetworkPage />;
      case 'caisse': return <LaCaissePage />;
      default: return <FleetDashboard onNavigate={setPage} />;
    }
  };

  return (
    <div style={{display:'flex',minHeight:'100vh'}}>
      <style>{STYLES}</style>
      <Sidebar active={page} onNavigate={setPage} user={user} onLogout={logout}/>
      <MobileHeader/>
      <MobileTabBar active={page} onNavigate={setPage}/>
      <main style={{marginInlineStart:'var(--sidebar-w)',flex:1,padding:'24px 32px',transition:'margin-left 0.2s ease'}}>
        {renderPage()}
      </main>
    </div>
  );
}
